import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { Button, Text } from '@/shared/ui';
import { CharacteristicsExtraFields } from './CharacteristicsExtraFields';
import { ConditionFields } from './ConditionFields';
import {
  getCategoryConfig,
  APARTMENT_CATEGORIES,
  LAND_CATEGORIES,
} from './schema';
import { useAgencyRequired } from './agency-required-context';
import type { PropertyFormValues } from './schema';
import type { CSSProperties } from 'react';

interface CharacteristicsSectionProps {
  form: UseFormReturn<PropertyFormValues>;
}

const labelKeyMap: Record<string, string> = {
  bedrooms: 'form.drawer.bedrooms',
  suites: 'form.drawer.suites',
  bathrooms: 'form.drawer.bathrooms',
  parking_spots: 'form.drawer.parkingSpots',
};

/**
 * Builds the inline style for a quantity selector button.
 * @returns The CSS properties for the button given its active state.
 */
const qtyButtonStyle = (active: boolean): CSSProperties => ({
  width: 36,
  height: 36,
  borderRadius: 'var(--radius-md)',
  border: active ? '1.5px solid var(--seed-base)' : '1px solid var(--border-subtle)',
  background: active ? 'var(--seed-base)' : 'var(--surface-secondary)',
  color: active ? 'var(--text-inverse)' : 'var(--text-primary)',
  fontSize: 'var(--text-small)',
  fontWeight: 600,
  fontFamily: 'var(--font-family)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface QuantitySelectorProps {
  field: string;
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  error?: string;
}

/**
 * Stepper-style selector for a small integer characteristic (0 to 5+). A new
 * property opens with nothing picked, so 0 is an answer the broker gives.
 *
 * @param props - Field key, label, current value, change handler and error.
 * @returns The rendered quantity selector.
 */
function QuantitySelector({ field, label, value, onChange, error }: QuantitySelectorProps) {
  const required = useAgencyRequired(field);
  const options = [0, 1, 2, 3, 4, 5];
  return (
    <div className="flex flex-col gap-(--space-1-5)" data-field={field}>
      <Text as="span" style={{ fontSize: 'var(--text-small)', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'var(--font-family)' }}>
        {label}
        {required && <Text as="span" style={{ marginLeft: 2, color: 'var(--status-error)' }}>*</Text>}
      </Text>
      <div className="flex gap-(--space-1-5)">
        {options.map(n => (
          <Button
            variant="none"
            disableSize
            key={n}
            type="button"
            onClick={() => onChange(n)}
            style={qtyButtonStyle(value === n)}
          >
            {n === 5 ? '5+' : n}
          </Button>
        ))}
      </div>
      {error && (
        <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--status-error)', fontFamily: 'var(--font-family)' }}>
          {error}
        </Text>
      )}
    </div>
  );
}

/**
 * Characteristics block of the property drawer form. Renders quantity selectors
 * for the category's numeric characteristics and delegates apartment/land-specific
 * extras to CharacteristicsExtraFields.
 *
 * @param props - The form instance driving the fields.
 * @returns The rendered section, or null when the category has no characteristics.
 */
export function CharacteristicsSection({ form }: CharacteristicsSectionProps) {
  const { t } = useTranslation('features/properties');
  const category = form.watch('category');
  const config = getCategoryConfig(category);

  // Land and garages count no rooms, but still have a condition and an
  // occupancy, so the section stays with those selects only.
  if (config.characteristics.length === 0) {
    return (
      <FormSection title={t('form.sections.characteristics')}>
        <ConditionFields form={form} />
      </FormSection>
    );
  }

  const isApartment = (APARTMENT_CATEGORIES as readonly string[]).includes(category);
  const isLand = (LAND_CATEGORIES as readonly string[]).includes(category);

  return (
    <FormSection title={t('form.sections.characteristics')}>
      <div className="flex flex-col gap-(--space-4)">
        {config.characteristics.map(field => (
          <Controller
            key={field}
            name={field}
            control={form.control}
            render={({ field: f, fieldState }) => (
              <QuantitySelector
                field={field}
                label={t(labelKeyMap[field])}
                value={f.value}
                onChange={v => f.onChange(v)}
                error={fieldState.error?.message}
              />
            )}
          />
        ))}
      </div>
      <CharacteristicsExtraFields form={form} isApartment={isApartment} isLand={isLand} />
      <ConditionFields form={form} />
    </FormSection>
  );
}
